// Models
import { MruApp } from "@ms/planner-models/lib/local/service/mru/MruApp";
import { ContainerType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
import { getContainerName } from "@ms/planner-stores/lib/local/utilities/ContainerUtilities";
import { compareByDisplayName, compareTemporalDate } from "@ms/uno-utilities/lib/local/Comparators";
/**
 * Method to get the channel name from teams store
 * @param channelId The channel ID
 * @param teamsChannelStore The teams channel store instance
 * @returns The channel display name or null
 */ export function getChannelName(channelId, teamsChannelStore) {
    return teamsChannelStore.getChannel(channelId)?.displayName ?? null;
}
/**
 * Method to get the container photo
 * @param id The container id
 * @param containerType The container type
 * @param modernGroupsStore The modern groups store instance
 * @returns The container photo URL or null
 */ export function getContainerPhoto(id, containerType, modernGroupsStore) {
    if (containerType === ContainerType.Group && id !== "") {
        return modernGroupsStore.getModernGroupPhotoById(id) ?? null;
    }
    return null;
}
/**
 * Method to get publishing capability
 * @param capabilitiesStore The capabilities store instance
 * @returns Whether publishing is enabled
 */ export function getPublishingCapability(capabilitiesStore) {
    if (!capabilitiesStore) {
        return false;
    }
    return capabilitiesStore.getIsPublishingEnabled().data;
}
/**
 * Method to get the breadcrumb information for Pinned items
 * @param mruItem The mru item
 * @param channelId The channel ID (optional)
 * @param containerStores The stores to use for lookup
 * @returns Array of breadcrumb strings
 */ export function getBreadcrumbItems(mruItem, channelId, containerStores) {
    const breadcrumbs = [];
    if (mruItem.app === MruApp.ToDo) return breadcrumbs;
    const containerType = mruItem.getContainerType();
    const containerId = mruItem.getContainerId();
    const channelName = channelId ? getChannelName(channelId, containerStores.teamsChannelStore) : null;
    const containerName = containerId && containerType ? getContainerName({
        containerId,
        containerType
    }, containerStores) : null;
    if (containerType === ContainerType.Group) {
        // If there is an associated channel, push the channel name
        channelName && breadcrumbs.push(channelName);
        // If the container is a group push the group name
        containerName && breadcrumbs.push(containerName);
    } else if (containerType === ContainerType.Roster) {
    // TODO: Get plan creation context from fetched plan
    }
    return breadcrumbs;
}
/**
 * Comparison function for sorting pinned items
 * @param a First MRU item
 * @param b Second MRU item
 * @returns Comparison result for sorting
 */ // TODO: Move this view base sorting to store [Task: 2727098]
export function comparePinnedItem(a, b) {
    const timeStampA = a.getLastPinnedTimeStamp();
    const timeStampB = b.getLastPinnedTimeStamp();
    // Sort first by lastPinnedTimeStamp. Items without a lastPinnedTimeStamp should go after the recent items.
    // Secondarily, sort by display name.
    if (timeStampA == null && timeStampB == null) {
        return compareByDisplayName(a, b, (item)=>item.title);
    }
    if (timeStampA == null) {
        return 1;
    }
    if (timeStampB == null) {
        return -1;
    }
    return compareTemporalDate(timeStampB, timeStampA);
}
/**
 * Method to check whether My Portfolios option should be visible for a user or not.
 * @param configProvider The config provider object
 * @param permissionsStore The permissions store instance
 * @param portfolioStore The portfolio store instance
 * @returns Whether the My Portfolios option should be visible
 */ export function checkForMyPortfolioOptionVisibility(configProvider, permissionsStore, portfolioStore) {
    // If the flight is not enabled, the user will not see the My Portfolios option irrespective of the license.
    const isPortfoliosExperienceEnabled = configProvider().flights.EnablePortfolioExperience;
    // If the environment is FR/DE, the user will not see the My Portfolios option.
    if (!isPortfoliosExperienceEnabled || !configProvider().settings.EnvironmentFeatureSettings.EnablePortfolioExperience || configProvider().settings.disableDataverse) {
        return false;
    }
    // Check if the user has the necessary license for accessing My Portfolios Option (P3+).
    if (permissionsStore.getUserPermissions().canCreatePortfolios) {
        return true;
    }
    // If user does not have P3+ license, but user was shared at least one portfolio, then user should see the My Portfolios option.
    const userPortfolioItems = portfolioStore.getPortfolioItems()?.data;
    const userHasPortfolioItems = userPortfolioItems && userPortfolioItems.length > 0;
    return !!userHasPortfolioItems;
}
